import React from "react";
import { useNavigate } from "react-router-dom";
import { IResetPassword } from "@interfaces/auth";
import { auth } from "@services/auth/auth-service";
import { handleApiError } from "@services/api";
import * as z from "zod";
import toast from "react-hot-toast";
import { Formik, FormikHelpers } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { Button, Input } from "@heroui/react";
import { Icon } from "@iconify/react";
import { AuthHeader } from "@/layouts/non-auth-layout";

const passwordResetSchema = z
  .object({
    password: z
      .string()
      .min(8, "Must be at least 8 characters")
      .min(1, "Password is required"),
    confirmPassword: z.string().min(1, "Confirm Password is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

const ResetPassword = () => {
  document.title = "Reset your password";

  const navigate = useNavigate();

  const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = React.useState(false);

  const onSubmit = async (
    values: IResetPassword,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    try {
      setSubmitting(true);
      await auth.resetPassword(values);
      toast.success("Password reset successfully");
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (e) {
      console.log(handleApiError(e));
      toast.error(handleApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <AuthHeader
        title="Reset your password"
        subtitle="Create a new password for your account" />

      <Formik
        initialValues={{
          password: "",
          confirmPassword: ""
        }}
        onSubmit={onSubmit}
        validationSchema={toFormikValidationSchema(passwordResetSchema)}>
        {({
          errors,
          touched,
          handleSubmit,
          handleChange,
          isSubmitting,
          values,
        }) => (
          <>
            <Input
              id="password"
              label="Password"
              name="password"
              placeholder="Enter your new password"
              type={isPasswordVisible ? "text" : "password"}
              variant="bordered"
              value={values.password}
              onChange={handleChange}
              isInvalid={errors?.password != null}
              errorMessage={touched?.password && errors?.password ? errors.password : ""}
              endContent={
                <button type="button" onClick={() => setIsPasswordVisible(prev => !prev)}>
                  {isPasswordVisible ? (
                    <Icon
                      className="pointer-events-none text-2xl text-default-400"
                      icon="solar:eye-closed-linear"
                    />
                  ) : (
                    <Icon
                      className="pointer-events-none text-2xl text-default-400"
                      icon="solar:eye-bold"
                    />
                  )}
                </button>
              }
            />
            <Input
              id="confirmPassword"
              label="Password"
              name="confirmPassword"
              placeholder="Confirm your new password"
              type={isConfirmPasswordVisible ? "text" : "password"}
              variant="bordered"
              value={values.confirmPassword}
              onChange={handleChange}
              isInvalid={errors?.confirmPassword != null}
              errorMessage={touched?.confirmPassword && errors?.confirmPassword ? errors.confirmPassword : ""}
              endContent={
                <button type="button" onClick={() => setIsConfirmPasswordVisible(prev => !prev)}>
                  {isConfirmPasswordVisible ? (
                    <Icon
                      className="pointer-events-none text-2xl text-default-400"
                      icon="solar:eye-closed-linear"
                    />
                  ) : (
                    <Icon
                      className="pointer-events-none text-2xl text-default-400"
                      icon="solar:eye-bold"
                    />
                  )}
                </button>
              }
            />
            <Button
              type="submit"
              isLoading={isSubmitting}
              onClick={() => handleSubmit()}
              style={{ backgroundColor: "#c6f648" }}>
              Continue
            </Button>
          </>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default ResetPassword;
